import React from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';

import type { PlayableAudio } from 'js/src/data/media';
import { getPlayingTrackDuration, getPlayingTrackUrl } from 'js/src/data/media';
import { getFormattedTimer } from 'js/src/libs/custom-utils';

type BaseTimeMarkerPresentationProps = {
    value?: number;
    readable?: boolean;
    className?: string;
};

const BaseTimeMarkerPresentation = ({
    value = 0,
    readable = true,
    className = '',
}: BaseTimeMarkerPresentationProps) => {
    const rootClassName = classNames('time-marker pe-none', className);
    const readableValue = readable ? getFormattedTimer(value * 1000, 'mm:ss') : value.toString();
    return <div className={rootClassName}>{readableValue}</div>;
};

// CurrentTimeMarker

type CurrentTimeMarkerPresentationProps = {
    media: PlayableAudio;
    className?: string;
};

export const CurrentTimeMarkerPresentation = ({
    media,
    className = '',
}: CurrentTimeMarkerPresentationProps) => {
    const playingTrackUrl = useSelector(getPlayingTrackUrl);
    const playingTrackDuration = useSelector(getPlayingTrackDuration);
    const nodeRef = React.useRef<HTMLDivElement>(null);

    if (playingTrackUrl !== media.mp3_url) {
        return null;
    }

    const rootClassName = classNames('current-time-marker', className);
    return (
        <CSSTransition nodeRef={nodeRef} timeout={50} classNames="fade-in-out">
            <div ref={nodeRef}>
                <BaseTimeMarkerPresentation
                    value={playingTrackDuration}
                    className={rootClassName}
                />
            </div>
        </CSSTransition>
    );
};

const CurrentTimeMarkerMemoized = React.memo(CurrentTimeMarkerPresentation);
export const CurrentTimeMarker = CurrentTimeMarkerMemoized;

// DurationTimeMarker

type DurationTimeMarkerPresentationProps = {
    media: PlayableAudio;
    className?: string;
};

export const DurationTimeMarkerPresentation = ({
    media,
    className = '',
}: DurationTimeMarkerPresentationProps) => {
    const duration = media.duration;
    const rootClassName = classNames('duration-marker', className);
    return <BaseTimeMarkerPresentation value={duration ?? undefined} className={rootClassName} />;
};

const DurationTimeMarkerMemoized = React.memo(DurationTimeMarkerPresentation);
export const DurationTimeMarker = DurationTimeMarkerMemoized;
